import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Heart, QrCode, RefreshCw } from 'lucide-react';
import { useAuthStore } from '../stores/useAuthStore';

export function SessionClosedPage() {
  const navigate = useNavigate();
  const { identifier, validateSession, setOrderTiming, setOrderMode } = useAuthStore();
  const [loading, setLoading] = useState(false);

  const handleStartNewOrder = async () => {
    if (!identifier) return;

    setLoading(true);
    try {
      const isValid = await validateSession(identifier);

      if (isValid) {
        const state = useAuthStore.getState();
        const { isTakeAway, tableId } = state;

        if (isTakeAway) {
          navigate(`/order-timing/${tableId || 'default'}`, { replace: true });
        } else {
          setOrderTiming('today');
          setOrderMode(null);
          navigate(`/dashboard/${tableId || 'default'}`, { replace: true });
        }
      } else {
        // Si falla validación, nos quedamos aca
        console.error("No se pudo iniciar nueva sesión");
      }
    } catch (e) {
      console.error("Error al iniciar orden", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: 'var(--color-bg-page)' }}>
      <div className="max-w-md w-full rounded-3xl shadow-2xl p-8 text-center animate-in fade-in zoom-in-95 duration-300" style={{ backgroundColor: 'var(--color-bg-card)' }}>
        {/* Icon */}
        <div className="w-20 h-20 bg-gradient-to-r from-sky-500 to-sky-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
          <Heart className="w-10 h-10 text-white" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          ¡Gracias por usar ComandaYa!
        </h1>

        {/* Message */}
        <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
          Esperamos que hayas disfrutado tu experiencia. <br />
          Tu sesión ha finalizado.
        </p>

        {/* Re-Order Button - Primary Action if identifier exists */}
        {identifier && (
          <div className="mb-4">
            <button
              onClick={handleStartNewOrder}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white px-6 py-3.5 rounded-xl font-bold transition-all duration-200 shadow-lg hover:shadow-sky-500/25 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Iniciando...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  <span>Pedir algo más</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Rating Link */}
        <div className="mb-8">
          <a
            href="https://comandaya.app/valoraciones"
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 font-semibold transition-colors ${!identifier ? 'bg-sky-50 dark:bg-sky-900/20 px-6 py-3 rounded-xl w-full justify-center' : ''}`}
          >
            <Star className="w-5 h-5" />
            Valorar Servicio
          </a>
        </div>

        {/* Instructions */}
        {!identifier && (
          <div className="bg-sky-50 dark:bg-sky-900/20 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <QrCode className="w-5 h-5 text-sky-600 dark:text-sky-400" />
              <span className="font-semibold text-sky-800 dark:text-sky-200">¿Querés seguir pidiendo?</span>
            </div>
            <p className="text-sm text-sky-700 dark:text-sky-300">
              Escanea el QR nuevamente para comenzar una nueva sesión.
            </p>
          </div>
        )}

        {/* Footer */}
        <p className="text-xs text-gray-500 dark:text-gray-500">
          ComandaYa - v0.1.0-beta
        </p>
      </div>
    </div>
  );
}